import React from 'react';
import { Layout } from '../../components/layout/Layout';
import { ScrollAnimation } from '../../components/common/ScrollAnimation';
import { Button } from '../../components/ui/Button';
import { Link } from 'react-router-dom';
import { MapPin, Plane, Camera, Star, Heart, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const destinations = [
  { name: 'Chennai', country: 'Tamil Nadu', emoji: '🌊', gradient: 'from-cyan-400 to-blue-500', image: '/images/destinations/chennai.webp', credit: { by: 'Irfanahmed605', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=48631939' } },
  { name: 'Mahabalipuram', country: 'Tamil Nadu', emoji: '🏛️', gradient: 'from-amber-400 to-orange-500', image: '/images/destinations/mahabalipuram.webp', credit: { by: 'Bernard Gagnon', license: 'CC BY-SA 3.0', page: 'https://commons.wikimedia.org/w/index.php?curid=7197749' } },
  { name: 'Pondicherry', country: 'Tamil Nadu', emoji: '🏖️', gradient: 'from-rose-400 to-pink-500', image: '/images/destinations/pondicherry.webp', credit: { by: 'Dey.sandip', license: 'CC BY 3.0', page: 'https://commons.wikimedia.org/w/index.php?curid=22581747' } },
  { name: 'Ooty', country: 'Tamil Nadu', emoji: '🌿', gradient: 'from-emerald-400 to-green-500', image: '/images/destinations/ooty.webp', credit: { by: 'Hellowikidoc', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=162480177' } },
  { name: 'Kodaikanal', country: 'Tamil Nadu', emoji: '🌄', gradient: 'from-violet-400 to-purple-500', image: '/images/destinations/kodaikanal.webp', credit: { by: 'Navaneethpp', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=88870385' } },
  { name: 'Yercaud', country: 'Tamil Nadu', emoji: '⛰️', gradient: 'from-teal-400 to-cyan-500', image: '/images/destinations/yercaud.webp', credit: { by: 'Sri2161k', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=119354909' } },
  { name: 'Coonoor', country: 'Tamil Nadu', emoji: '🍃', gradient: 'from-lime-400 to-emerald-500', image: '/images/destinations/coonoor.webp', credit: { by: 'Niranjan Ramamurthy', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=196289771' } },
  { name: 'Madurai', country: 'Tamil Nadu', emoji: '🛕', gradient: 'from-orange-400 to-red-500', image: '/images/destinations/madurai.webp', credit: { by: 'MADHURANTHAKAN JAGADEESAN', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=51779947' } },
  { name: 'Thanjavur', country: 'Tamil Nadu', emoji: '🛕', gradient: 'from-yellow-400 to-amber-500', image: '/images/destinations/thanjavur.webp', credit: { by: 'Rainer Halama', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=176922407' } },
  { name: 'Rameswaram', country: 'Tamil Nadu', emoji: '🌅', gradient: 'from-pink-400 to-rose-500', image: '/images/destinations/rameswaram.webp', credit: { by: 'Shubham Gupta', license: 'CC BY-SA 3.0', page: 'https://commons.wikimedia.org/w/index.php?curid=21346220' } },
  { name: 'Kanyakumari', country: 'Tamil Nadu', emoji: '🌊', gradient: 'from-indigo-400 to-blue-500', image: '/images/destinations/kanyakumari.webp', credit: { by: 'Ankita Patil (Mukdam)', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=82135670' } },
  { name: 'Coimbatore', country: 'Tamil Nadu', emoji: '🌺', gradient: 'from-fuchsia-400 to-pink-500', image: '/images/destinations/coimbatore.webp', credit: { by: 'Prabhuthiru57', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=72964997' } },
  { name: 'Pollachi', country: 'Tamil Nadu', emoji: '🌴', gradient: 'from-green-400 to-teal-500', image: '/images/destinations/pollachi.webp', credit: { by: 'Divyacskn1289', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=49323050' } },
  { name: 'Valparai', country: 'Tamil Nadu', emoji: '☁️', gradient: 'from-sky-400 to-cyan-500', image: '/images/destinations/valparai.webp', credit: { by: 'Rohitjahnavi', license: 'CC BY-SA 4.0', page: 'https://commons.wikimedia.org/w/index.php?curid=98777371' } },
  { name: 'Hogenakkal', country: 'Tamil Nadu', emoji: '💦', gradient: 'from-blue-400 to-indigo-500', image: '/images/destinations/hogenakkal.webp', credit: { by: 'Mithun Kundu', license: 'CC BY-SA 3.0', page: 'https://commons.wikimedia.org/w/index.php?curid=11408566' } },
];

const features = [
  { icon: Plane, title: 'We Travel to You', desc: 'We travel to your chosen destination anywhere in Tamil Nadu to capture your special day.', gradient: 'from-cyan-500 to-blue-600', shadow: 'shadow-cyan-400/30', bg: 'from-cyan-50 to-blue-50', border: 'border-cyan-200' },
  { icon: Camera, title: 'Expert Eye', desc: 'Expert knowledge of scenic locations, lighting, and best shooting spots at every destination.', gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-400/30', bg: 'from-violet-50 to-purple-50', border: 'border-violet-200' },
  { icon: MapPin, title: 'Iconic Backdrops', desc: 'Breathtaking scenery — temples, hills, beaches, and gardens — create unforgettable albums.', gradient: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-400/30', bg: 'from-rose-50 to-pink-50', border: 'border-rose-200' },
  { icon: Star, title: 'Cinematic Quality', desc: '4K videography and high-resolution photography delivered with cinematic post-processing.', gradient: 'from-amber-500 to-orange-600', shadow: 'shadow-amber-400/30', bg: 'from-amber-50 to-orange-50', border: 'border-amber-200' },
];

export const DestinationWeddingsPage: React.FC = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="relative text-white py-28 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0c0a47 0%, #1e1b4b 30%, #4c1d95 60%, #701a75 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-10 left-10 w-80 h-80 rounded-full blur-3xl opacity-25"
            style={{ background: '#d946ef' }}
            animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute bottom-10 right-10 w-96 h-96 rounded-full blur-3xl opacity-20"
            style={{ background: '#06b6d4' }}
            animate={{ x: [0, -50, 0], y: [0, -30, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 w-72 h-72 rounded-full blur-3xl opacity-15"
            style={{ background: '#f43f5e' }}
            animate={{ x: [0, 40, 0], y: [0, -50, 0] }}
            transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
          />
        </div>
        <div className="h-1.5 absolute top-0 left-0 right-0 bg-gradient-to-r from-cyan-400 via-violet-400 via-rose-400 to-amber-400" />

        {/* Floating location pins */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[
            { top: '15%', left: '8%', color: 'text-cyan-300/25', size: 'w-10 h-10' },
            { top: '30%', right: '10%', color: 'text-rose-300/20', size: 'w-8 h-8' },
            { bottom: '25%', left: '15%', color: 'text-amber-300/20', size: 'w-12 h-12' },
            { top: '60%', right: '20%', color: 'text-violet-300/20', size: 'w-9 h-9' },
          ].map((p, i) => (
            <motion.div
              key={i}
              className={`absolute ${p.size} ${p.color}`}
              style={{ top: p.top, left: p.left, right: p.right, bottom: p.bottom } as any}
              animate={{ y: [0, -20, 0], rotate: [0, 15, -15, 0] }}
              transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut' }}
            >
              <MapPin className="w-full h-full" />
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={false}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold mb-6 border border-cyan-400/40"
              style={{ background: 'rgba(6,182,212,0.15)', color: '#67e8f9' }}
            >
              <Plane className="h-4 w-4" /> Across Tamil Nadu & Beyond
            </motion.div>
            <h1 className="text-4xl md:text-7xl font-bold mb-6 leading-tight">
              Destination{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #67e8f9, #f0abfc, #fda4af)' }}>
                Weddings
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-purple-200 max-w-3xl mx-auto leading-relaxed">
              Capture your dream wedding against the most breathtaking backdrops —
              from misty hill stations to golden beaches and ancient temples.
            </p>
            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/contact">
                <Button variant="secondary" size="lg" className="shadow-xl shadow-amber-500/30 hover:scale-105 transition-transform">
                  Plan Your Destination Wedding
                </Button>
              </Link>
              <Link to="/gallery">
                <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/15 hover:scale-105 transition-transform backdrop-blur-sm">
                  View Gallery
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #ecfeff 0%, #fdf4ff 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 gradient-text">Why Choose Destination Photography?</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              The perfect backdrop makes your wedding photos truly extraordinary
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, index) => (
              <ScrollAnimation key={f.title} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -12, scale: 1.04 }}
                  className={`bg-gradient-to-br ${f.bg} border ${f.border} p-7 rounded-2xl shadow-lg ${f.shadow} hover:shadow-2xl transition-all text-center`}
                >
                  <div className={`bg-gradient-to-br ${f.gradient} rounded-2xl p-4 w-16 h-16 mx-auto mb-5 flex items-center justify-center shadow-lg ${f.shadow}`}>
                    <f.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-gray-800">{f.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Colorful divider */}
      <div className="h-2 w-full bg-gradient-to-r from-cyan-500 via-violet-500 via-rose-500 to-amber-500" />

      {/* Destinations Grid */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 50%, #f5d0fe 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 gradient-text">Popular Destinations</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto text-lg">
              We travel across Tamil Nadu to capture your love story in stunning locations
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {destinations.map((dest, index) => (
              <ScrollAnimation key={index} direction="up" delay={(index % 5) * 0.08}>
                <motion.div
                  whileHover={{ y: -12, scale: 1.05 }}
                  className="relative rounded-2xl overflow-hidden shadow-lg cursor-pointer group"
                >
                  <div className="aspect-[3/4] relative">
                    <img
                      src={dest.image}
                      alt={dest.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    {/* Gradient fallback */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${dest.gradient} items-center justify-center hidden`}
                    >
                      <span className="text-4xl">{dest.emoji}</span>
                    </div>
                    {/* Always-visible gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-lg">{dest.emoji}</span>
                        <h3 className="font-bold text-sm">{dest.name}</h3>
                      </div>
                      <p className="text-white/70 text-xs">{dest.country}</p>
                    </div>
                    {/* Hover overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${dest.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-300`} />
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow-md">
                        <Heart className="h-4 w-4 text-rose-500" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
          <p className="mt-8 text-center text-xs text-gray-500 leading-relaxed">
            Destination photos from Wikimedia Commons:{' '}
            {destinations.map((dest, i) => (
              <React.Fragment key={dest.name}>
                <a href={dest.credit.page} target="_blank" rel="noopener noreferrer" className="underline hover:text-primary-600">
                  {dest.name}
                </a>{' '}
                by {dest.credit.by} ({dest.credit.license}){i < destinations.length - 1 ? ' · ' : ''}
              </React.Fragment>
            ))}
          </p>
        </div>
      </section>

      {/* Gallery Strip */}
      <section className="py-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 50%, #701a75 100%)' }}>
        <div className="h-1 absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-400 via-rose-400 to-cyan-400" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl font-bold text-center text-white mb-3">
              Destination{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #fda4af, #fcd34d)' }}>
                Gallery
              </span>
            </h2>
            <p className="text-center text-purple-300 mb-10">Real moments from our destination shoots</p>
          </ScrollAnimation>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { url: 'https://images.unsplash.com/photo-1537633552985-df8429e8048b?auto=format&fit=crop&w=500&q=80', label: 'Wedding by the Beach' },
              { url: 'https://images.unsplash.com/photo-1604017011826-d3b4c23f8914?auto=format&fit=crop&w=500&q=80', label: 'Hill Station Romance' },
              { url: 'https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?auto=format&fit=crop&w=500&q=80', label: 'Grand Reception' },
              { url: 'https://images.unsplash.com/photo-1523438885200-e635ba2c371e?auto=format&fit=crop&w=500&q=80', label: 'Garden Ceremony' },
              { url: 'https://images.unsplash.com/photo-1532712938310-34cb3982ef74?auto=format&fit=crop&w=500&q=80', label: 'Countryside Walk' },
              { url: 'https://images.unsplash.com/photo-1621621667797-e06afc217fb0?auto=format&fit=crop&w=500&q=80', label: 'Pre-Wedding Outdoors' },
              { url: 'https://images.unsplash.com/photo-1494774157365-9e04c6720e47?auto=format&fit=crop&w=500&q=80', label: 'Sunset Portraits' },
              { url: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?auto=format&fit=crop&w=500&q=80', label: 'Open-Air Aisle' },
            ].map((img, i) => (
              <ScrollAnimation key={i} direction="up" delay={i * 0.06}>
                <motion.div
                  whileHover={{ scale: 1.06, rotate: i % 2 === 0 ? 1.5 : -1.5 }}
                  className="aspect-square rounded-2xl overflow-hidden shadow-xl cursor-pointer group relative"
                >
                  <img
                    src={img.url}
                    alt={img.label}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-3 left-3 right-3 text-white text-xs font-semibold">{img.label}</div>
                  </div>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Strip */}
      <section className="py-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fdf4ff 50%, #ecfeff 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Arun & Priya', location: 'Ooty', text: 'The misty mountains of Ooty were the perfect backdrop. Every frame was magical!', gradient: 'from-emerald-500 to-teal-600' },
              { name: 'Karthik & Divya', location: 'Pondicherry', text: 'Beach vibes, French architecture, and AVC — our destination wedding was a dream come true.', gradient: 'from-cyan-500 to-blue-600' },
              { name: 'Raj & Meena', location: 'Kodaikanal', text: 'The cloud forests and misty lakeside photos are something we\'ll cherish forever.', gradient: 'from-violet-500 to-purple-600' },
            ].map((t, i) => (
              <ScrollAnimation key={i} direction="up" delay={i * 0.1}>
                <motion.div
                  whileHover={{ y: -8 }}
                  className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic mb-4 text-sm leading-relaxed">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${t.gradient} flex items-center justify-center text-white font-bold text-sm shadow`}>
                      {t.name[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                      <p className="text-gray-500 text-xs flex items-center gap-1"><MapPin className="h-3 w-3" />{t.location}</p>
                    </div>
                  </div>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0c0a47 0%, #4c1d95 50%, #701a75 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20 -mr-48 -mt-48" style={{ background: '#67e8f9' }} />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-15 -ml-48 -mb-48" style={{ background: '#fda4af' }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <ScrollAnimation direction="fade">
            <div className="mb-6 text-6xl">✈️</div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Ready to Plan Your{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #67e8f9, #fda4af)' }}>
                Destination Wedding?
              </span>
            </h2>
            <p className="text-xl text-purple-200 mb-8 max-w-2xl mx-auto">
              Tell us your dream location and we'll create memories that last a lifetime
            </p>
            <Link to="/contact">
              <Button variant="secondary" size="lg" className="shadow-2xl shadow-amber-500/30 hover:scale-105 transition-transform">
                <Sparkles className="h-5 w-5 mr-2" /> Start Planning Today
              </Button>
            </Link>
          </ScrollAnimation>
        </div>
      </section>
    </Layout>
  );
};
